import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Send, ShieldCheck, TerminalSquare, BookMarked, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";

const Bold = ({ text }) => {
  const parts = String(text).split(/\*\*(.+?)\*\*/g);
  return parts.map((p, i) => (i % 2 ? <strong key={i} className="font-semibold text-[var(--brand-primary)]">{p}</strong> : p));
};

const AnswerText = ({ text }) => (
  <div className="space-y-2">
    {String(text).split("\n").filter((l) => l.trim()).map((line, i) =>
      line.trim().startsWith("- ") ? (
        <p key={i} className="text-[13px] text-slate-700 leading-relaxed pl-3 border-l-2 border-teal-600">
          <Bold text={line.trim().slice(2)} />
        </p>
      ) : (
        <p key={i} className="text-[13px] text-slate-700 leading-relaxed"><Bold text={line} /></p>
      ))}
  </div>
);

export default function OpenQueryPage() {
  const { user } = useAuth();
  const [question, setQuestion] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [history, setHistory] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exhibits, setExhibits] = useState([]);
  const allowed = ["admin", "hr_manager", "dept_head"].includes(user.role);
  const canExhibit = ["admin", "hr_manager"].includes(user.role);

  const loadHistory = () => api.get("/query/history").then((r) => setHistory(r.data.queries)).catch(() => setHistory([]));
  const loadExhibits = () => api.get("/query/board-pack/exhibits").then((r) => setExhibits(r.data.exhibits)).catch(() => {});
  useEffect(() => {
    if (!allowed) return;
    api.get("/query/suggestions").then((r) => setSuggestions(r.data.suggestions)).catch(() => {});
    loadHistory();
    if (canExhibit) loadExhibits();
  }, [allowed]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!allowed) {
    return (
      <div className="border border-border bg-white p-10 text-center" data-testid="query-restricted">
        <Lock className="h-6 w-6 mx-auto text-muted-foreground mb-3" />
        <p className="text-[14px] text-slate-700">Open Query is available to Administrators, HR Managers and Department Heads.</p>
      </div>
    );
  }

  const run = async (q) => {
    const text = (q || question).trim();
    if (text.length < 8) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/query", { question: text });
      setResult(r.data);
      loadHistory();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addToBoardPack = async () => {
    try {
      await api.post(`/query/${result.id}/board-pack`);
      toast.success("Exhibit added — it will render in the next Board Pack download");
      loadExhibits();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteExhibit = async (id) => {
    try {
      await api.delete(`/query/board-pack/exhibits/${id}`);
      toast.success("Exhibit removed from the board pack");
      loadExhibits();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 fade-in items-start" data-testid="open-query-page">
      <div className="xl:col-span-2 space-y-4">
        <div className="bg-white border border-border p-5">
          <div className="flex items-center gap-2 mb-3">
            <TerminalSquare className="h-4 w-4 text-teal-600" />
            <p className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--brand-primary)]">
              Ask anything about your workforce
            </p>
            {user.role === "dept_head" && (
              <span className="text-[10px] font-mono border border-slate-300 text-slate-500 px-1.5 py-0.5 ml-auto">
                SCOPED TO YOUR DEPARTMENT
              </span>
            )}
          </div>
          <Textarea data-testid="query-input" value={question} rows={3}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) run(); }}
            placeholder="e.g. Which departments have the highest compliance risk next quarter?"
            className="rounded-none text-[13.5px]" />
          <div className="flex items-center justify-between mt-3">
            <p className="text-[11px] text-muted-foreground">
              Retrieval is role-scoped · outbound payloads are PII-tokenized · answers cite their data sources.
            </p>
            <Button data-testid="query-submit-btn" onClick={() => run()} disabled={busy || question.trim().length < 8}
              className="rounded-none h-9 text-[12px] bg-teal-600 hover:bg-teal-700">
              <Send className="h-3.5 w-3.5" /> {busy ? "Analyzing…" : "Run query"}
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="query-suggestions">
          {suggestions.map((s, i) => (
            <button key={i} data-testid={`suggestion-chip-${i}`} onClick={() => { setQuestion(s); run(s); }}
              disabled={busy}
              className="text-[11.5px] text-slate-600 bg-white border border-border px-3 py-1.5 hover:border-teal-600 hover:text-teal-800 transition-colors text-left">
              {s}
            </button>
          ))}
        </div>

        {busy && (
          <div className="bg-white border border-border p-5 space-y-3" data-testid="query-loading">
            <div className="flex items-center gap-2 text-[12px] text-teal-700">
              <span className="h-2 w-2 bg-teal-600 animate-pulse" /> Retrieving role-scoped data → masking → querying model…
            </div>
            <Skeleton className="h-4 w-3/4 rounded-none" />
            <Skeleton className="h-4 w-2/3 rounded-none" />
            <Skeleton className="h-4 w-4/5 rounded-none" />
          </div>
        )}

        {result && !busy && (
          <div className="bg-white border border-border" data-testid="query-answer-card">
            <div className="px-5 py-3 border-b border-border">
              <p className="text-[13px] font-medium text-[var(--brand-primary)]">{result.question}</p>
            </div>
            <div className="px-5 py-4">
              <AnswerText text={result.answer} />
              {result.caveats && (
                <p className="text-[11.5px] text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 mt-3">
                  {result.caveats}
                </p>
              )}
            </div>
            <div className="px-5 py-3 border-t border-border flex items-center gap-2 flex-wrap">
              {(result.citations || []).map((c) => (
                <span key={c} data-testid={`citation-chip-${c.replace(/\s+/g, "-").toLowerCase()}`}
                  className="text-[10px] font-mono bg-teal-50 border border-teal-600/30 text-teal-800 px-2 py-0.5">
                  {c}
                </span>
              ))}
              {canExhibit && (
                <button data-testid="add-to-boardpack-btn" onClick={addToBoardPack}
                  disabled={exhibits.some((x) => x.source_query_id === result.id)}
                  className="inline-flex items-center gap-1.5 text-[10.5px] font-medium text-teal-700 border border-teal-600/40 px-2 py-1 hover:bg-teal-50 disabled:opacity-50 disabled:cursor-not-allowed">
                  <BookMarked className="h-3 w-3" />
                  {exhibits.some((x) => x.source_query_id === result.id) ? "In Board Pack" : "Add to Board Pack"}
                </button>
              )}
              <span data-testid="query-audit-chip"
                className="inline-flex items-center gap-1.5 text-[10px] font-mono text-slate-500 ml-auto">
                <ShieldCheck className="h-3 w-3 text-teal-600" />
                {result.entities_masked_count} entities tokenized · audit {String(result.audit_id).slice(0, 8)} · {result.model} · {result.latency_ms} ms
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="bg-white border border-border" data-testid="query-history">
          <div className="px-4 py-3 border-b border-border">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Your query history</p>
          </div>
          {history === null && <Skeleton className="h-40 m-4 rounded-none" />}
          {history?.length === 0 && (
            <p className="px-4 py-8 text-center text-[12px] text-muted-foreground">No queries yet.</p>
          )}
          <div className="divide-y divide-border max-h-[400px] overflow-y-auto">
            {(history || []).map((h) => (
              <button key={h.id} data-testid={`history-item-${h.id}`} onClick={() => { setResult(h); setQuestion(h.question); }}
                className="w-full text-left px-4 py-3 hover:bg-slate-50">
                <p className="text-[12.5px] text-slate-800 leading-snug">{h.question}</p>
                <p className="text-[10px] font-mono text-slate-400 mt-1">
                  {String(h.created_at).slice(0, 16).replace("T", " ")} · {(h.citations || []).join(", ")}
                </p>
              </button>
            ))}
          </div>
        </div>

        {canExhibit && (
          <div className="bg-white border border-border" data-testid="boardpack-exhibits">
            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
              <BookMarked className="h-3.5 w-3.5 text-teal-600" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Board Pack exhibits ({exhibits.length})
              </p>
            </div>
            {exhibits.length === 0 && (
              <p className="px-4 py-6 text-center text-[12px] text-muted-foreground">
                No exhibits yet — promote an answer with "Add to Board Pack".
              </p>
            )}
            <div className="divide-y divide-border max-h-[280px] overflow-y-auto">
              {exhibits.map((x) => (
                <div key={x.id} className="px-4 py-2.5 flex items-start gap-2" data-testid={`exhibit-item-${x.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] text-slate-800 leading-snug truncate" title={x.question}>{x.question}</p>
                    <p className="text-[10px] font-mono text-slate-400 mt-0.5">
                      {String(x.created_at).slice(0, 10)} · {(x.citations || []).join(", ")} · by {x.added_by}
                    </p>
                  </div>
                  {user.role === "admin" && (
                    <button data-testid={`exhibit-delete-${x.id}`} onClick={() => deleteExhibit(x.id)}
                      className="text-slate-400 hover:text-red-600 shrink-0 mt-0.5" title="Remove exhibit">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
